/**
 * WSH Bundle Builder Page — v4
 *
 * Cart-as-source-of-truth architecture. Bundle state lives in the Shopify cart
 * via _bundle_token line item properties. The builder reads the cart on load
 * and writes through on every ADD/REMOVE. No ephemeral in-memory staging.
 *
 * - Cart reconstruction on page load (GET /cart.js)
 * - Individual add-to-cart on each + ADD click (POST /cart/add.js)
 * - Individual remove on each × click (POST /cart/change.js)
 * - 5-state bundle summary (desktop sidebar + mobile sheet)
 * - Mobile slim bar + expandable sheet with close X
 * - MutationObserver for infinite scroll card injection
 * - filter:update listener for re-injection + layout re-attach after AJAX DOM swaps
 * - Multi-currency safe: prices from cart API, never parsed from DOM text
 */
(() => {
  'use strict';

  /* ========================================================================
     State — synced with cart, not ephemeral
     ======================================================================== */
  const bundle = {
    items: [], // { variantId, productId, title, originalPrice, discountedPrice, handle, imageUrl, productUrl, lineItemKey }
  };
  let activeBundleToken = null; // reused for all adds in the same bundle group

  /* ========================================================================
     Helpers
     ======================================================================== */

  /* --- Multi-currency safe price formatting (Standing Rule 1) ---
     Uses Shopify.formatMoney when available, falls back to Intl.NumberFormat.
     The money format string is injected by wsh-bundle-grid.liquid. */
  const shopMoneyFormat = window.__wshBundleMoneyFormat || '${{amount}}';

  function formatMoney(cents) {
    if (window.Shopify?.formatMoney) {
      return Shopify.formatMoney(cents, shopMoneyFormat);
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD',
    }).format(cents / 100);
  }

  /* --- Product price cache (Standing Rule 1) ---
     Keyed by product handle. Stores { price (cents), currency }.
     Invalidated on currency switch so re-fetch returns presentment prices. */
  const priceCache = new Map();
  let priceCacheCurrency = window.Shopify?.currency?.active || 'USD';

  function invalidatePriceCacheIfCurrencyChanged() {
    const current = window.Shopify?.currency?.active || 'USD';
    if (current !== priceCacheCurrency) {
      priceCache.clear();
      priceCacheCurrency = current;
      return true;
    }
    return false;
  }

  async function fetchProductPrice(handle) {
    invalidatePriceCacheIfCurrencyChanged();
    if (priceCache.has(handle)) return priceCache.get(handle);
    try {
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) return 0;
      const data = await res.json();
      const price = data.variants?.[0]?.price || 0;
      priceCache.set(handle, price);
      return price;
    } catch {
      return 0;
    }
  }

  /* Re-read cart after currency switch — cart prices update automatically */
  async function refreshBundlePrices() {
    await readBundleFromCart();
  }

  /* ========================================================================
     Cart API — source of truth (Standing Rule 1 for prices)
     ======================================================================== */

  /** Read cart, populate bundle.items from _bundle_token line items */
  async function readBundleFromCart() {
    try {
      const res = await fetch('/cart.js');
      if (!res.ok) return;
      const cart = await res.json();

      bundle.items = [];
      activeBundleToken = null;

      for (const item of cart.items) {
        const token = item.properties?._bundle_token;
        if (!token) continue;

        // Lock to the first bundle group found
        if (!activeBundleToken) activeBundleToken = token;
        if (token !== activeBundleToken) continue;

        bundle.items.push({
          variantId: String(item.variant_id),
          productId: String(item.product_id),
          title: item.product_title,
          originalPrice: item.original_line_price / item.quantity, // base price before discounts
          discountedPrice: item.final_line_price / item.quantity,  // after cart discounts
          handle: item.handle || '',
          imageUrl: item.featured_image?.url ? item.featured_image.url.replace(/(\.\w+)$/, '_200x$1') : '',
          productUrl: item.url || '',
          lineItemKey: item.key,
        });
      }

      syncAllButtons();
      updateAllSummaries();

      if (bundle.items.length > 0) {
        showContinuationBanner();
      }
    } catch (err) {
      console.error('Bundle cart read failed:', err);
    }
  }

  /** Add a single product to cart with the active bundle token */
  async function addItemToCart(variantId) {
    if (!activeBundleToken) {
      activeBundleToken = `bundle_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    }

    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{
          id: parseInt(variantId, 10),
          quantity: 1,
          properties: {
            '_bundle_token': activeBundleToken,
            '_bundle_page': 'build-your-bundle',
            '_bundle_source': 'byob_page',
          },
        }],
      }),
    });

    if (!res.ok) throw new Error(`Cart add failed: ${res.status}`);
    const data = await res.json();
    dispatchCartUpdate(data);
    return data.items?.[0] || null;
  }

  /** Remove a line item from cart by its key */
  async function removeItemFromCart(lineItemKey) {
    if (!lineItemKey) return;
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lineItemKey, quantity: 0 }),
    });
    if (!res.ok) throw new Error(`Cart remove failed: ${res.status}`);
    const cart = await res.json();
    dispatchCartUpdate(cart);
    return cart;
  }

  /** Dispatch cart:update for Horizon badge/drawer sync — does NOT open drawer */
  function dispatchCartUpdate(cartData) {
    const event = new Event('cart:update', { bubbles: true });
    event.detail = {
      resource: cartData,
      sourceId: 'wsh-bundle-page',
      data: { source: 'wsh-bundle-page', sections: cartData?.sections || {} },
    };
    document.dispatchEvent(event);
  }

  /** Show continuation banner when bundle found in cart on page load */
  function showContinuationBanner() {
    const sidebar = document.querySelector('[data-wsh-bundle-sidebar] .wsh-bp__summary');
    if (!sidebar || sidebar.querySelector('.wsh-bp__continuation')) return;

    const count = bundle.items.length;
    const message = count >= 4
      ? 'Your bundle is saved in your cart. Ready when you are.'
      : 'Your bundle is saved in your cart. Keep adding to unlock deeper discounts.';

    const banner = document.createElement('div');
    banner.className = 'wsh-bp__continuation';
    banner.innerHTML = `
      <span class="wsh-bp__continuation-text">${message}</span>
      <button class="wsh-bp__continuation-close" type="button" aria-label="Dismiss">&times;</button>
    `;
    banner.querySelector('.wsh-bp__continuation-close').addEventListener('click', () => banner.remove());
    sidebar.insertBefore(banner, sidebar.firstChild);
  }

  function getDiscountPct(count) {
    if (count >= 4) return 20;
    if (count >= 3) return 15;
    if (count >= 2) return 10;
    return 0;
  }

  function escapeHtml(str) {
    const el = document.createElement('span');
    el.textContent = str;
    return el.innerHTML;
  }

  /* State copy helpers */
  const STATE_COPY = {
    0: {
      title: 'Build your bundle',
      subcopy: 'Add 2 packs for 10% off',
      cta: 'Select packs to start',
      ctaDisabled: true,
      encourage: 'Add 2 packs to activate bundle pricing',
      mobileStatus: '0 packs \u00b7 Build your bundle',
      mobileNudge: 'Add 2 packs for 10%',
    },
    1: {
      title: '1 pack added',
      subcopy: 'Add 1 more for 10% off',
      cta: 'Add 1 more pack',
      ctaDisabled: true,
      encourage: 'Add 1 more for 10% off',
      mobileStatus: '1 pack \u00b7 No discount yet',
      mobileNudge: 'Add 1 more for 10%',
    },
    2: {
      title: '10% off unlocked',
      subcopy: 'Add 1 more for 15% off',
      cta: null, // dynamic: "View Cart — $X.XX"
      ctaDisabled: false,
      encourage: null, // duplicated subcopy — removed to reclaim footer space
      mobileStatus: '2 packs \u00b7 10% off',
      mobileNudge: 'Add 1 more for 15%',
    },
    3: {
      title: '15% off unlocked',
      subcopy: 'Add 1 more for 20% off',
      cta: null, // dynamic: "View Cart — $X.XX"
      ctaDisabled: false,
      encourage: null, // duplicated subcopy — removed to reclaim footer space
      mobileStatus: '3 packs \u00b7 15% off',
      mobileNudge: 'Add 1 more for 20%',
    },
    4: {
      title: '20% off unlocked',
      subcopy: 'Maximum discount \u2014 keep adding to grow your collection',
      cta: null, // dynamic: "View Cart — $X.XX"
      ctaDisabled: false,
      encourage: null,
      mobileStatus: null,
      mobileNudge: 'Maximum discount',
    },
  };

  function getStateCopy(count) {
    if (count >= 4) return STATE_COPY[4];
    return STATE_COPY[count] || STATE_COPY[0];
  }

  const $ = (sel) => document.querySelector(sel);
  const $$ = (sel) => document.querySelectorAll(sel);

  /* ========================================================================
     ADD button injection into native Horizon product cards
     ======================================================================== */
  function injectAddButtons() {
    const cards = $$('.product-grid__item');
    cards.forEach((card) => {
      // Skip if already injected
      if (card.querySelector('.wsh-bp__add-btn')) return;

      const productId = card.dataset.productId;
      if (!productId) return;

      // Extract product data from the card (no price parsing — Standing Rule 1)
      const link = card.querySelector('a[href*="/products/"]');
      const titleEl = card.querySelector('.product-card__content [class*="product-title"], .product-card__content h3, .product-card__content .rte');
      const imgEl = card.querySelector('img');

      const productUrl = link ? link.getAttribute('href') : '';
      const title = titleEl ? titleEl.textContent.trim() : '';
      const imageUrl = imgEl ? (imgEl.currentSrc || imgEl.src || '') : '';

      // Extract product handle from link for price fetching
      const handleMatch = productUrl.match(/\/products\/([^/?#]+)/);
      const handle = handleMatch ? handleMatch[1] : '';

      // Get variant ID from Quick Add form
      const quickAddForm = card.querySelector('form[action*="/cart/add"]');
      const variantInput = quickAddForm?.querySelector('input[name="id"]');
      const variantId = variantInput ? variantInput.value : '';

      // Create the ADD button — price fetched lazily on click, not from DOM text
      const btn = document.createElement('button');
      btn.className = 'wsh-bp__add-btn';
      btn.type = 'button';
      btn.setAttribute('data-wsh-bundle-add', '');
      btn.setAttribute('data-product-id', productId);
      btn.setAttribute('data-variant-id', variantId);
      btn.setAttribute('data-product-title', title);
      btn.setAttribute('data-product-handle', handle);
      btn.setAttribute('data-product-image', imageUrl.includes('?') ? imageUrl.split('?')[0] + '?width=200' : imageUrl);
      btn.setAttribute('data-product-url', productUrl);
      btn.setAttribute('aria-label', `Add ${title} to bundle`);

      const inBundle = bundle.items.some((i) => i.productId === productId);
      if (inBundle) {
        btn.classList.add('wsh-bp__add-btn--added');
        btn.setAttribute('aria-label', `Remove ${title} from bundle`);
      }

      btn.innerHTML = `<span data-add-label>${inBundle ? '\u2713 ADDED' : '+ ADD'}</span>`;

      // Append after the product card content
      card.appendChild(btn);
    });
  }

  /* ========================================================================
     ADD / ADDED button handling (event delegation)
     ======================================================================== */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-wsh-bundle-add]');
    if (!btn || btn.dataset.wshBusy) return;
    e.preventDefault();
    e.stopPropagation();

    const productId = btn.dataset.productId;
    const variantId = btn.dataset.variantId;
    const idx = bundle.items.findIndex((i) => i.productId === productId);

    btn.dataset.wshBusy = '1'; // prevent rapid double-click

    try {
      if (idx >= 0) {
        // Remove from cart
        await removeItemFromCart(bundle.items[idx].lineItemKey);
      } else {
        // Quantity-one check: already in bundle?
        if (bundle.items.some((i) => i.variantId === variantId)) {
          delete btn.dataset.wshBusy;
          return;
        }
        // Add to cart
        await addItemToCart(variantId);
      }
      // Re-read cart for authoritative prices (original + discounted)
      // This rebuilds bundle.items, syncs buttons, updates summaries
      await readBundleFromCart();
    } catch (err) {
      console.error('Bundle cart operation failed:', err);
    } finally {
      delete btn.dataset.wshBusy;
    }

    showMobileFlash();
  });

  /* Sync button visual states from in-memory bundle */
  function syncAllButtons() {
    $$('[data-wsh-bundle-add]').forEach((btn) => {
      const inBundle = bundle.items.some((i) => i.productId === btn.dataset.productId);
      btn.classList.toggle('wsh-bp__add-btn--added', inBundle);
      const label = btn.querySelector('[data-add-label]');
      if (label) label.textContent = inBundle ? '\u2713 ADDED' : '+ ADD';
      btn.setAttribute('aria-label', inBundle
        ? `Remove ${btn.dataset.productTitle} from bundle`
        : `Add ${btn.dataset.productTitle} to bundle`
      );
    });
  }

  /* ========================================================================
     Intercept Horizon's Quick Add — redirect to bundle instead of cart.
     Horizon Quick Add uses form submission or fetch to /cart/add.js.
     We intercept clicks on Quick Add buttons and form submits.
     ======================================================================== */
  function interceptQuickAdd() {
    // Intercept Quick Add button clicks within product cards
    document.addEventListener('click', (e) => {
      const quickAddBtn = e.target.closest('.product-grid__item .quick-add-button, .product-grid__item [data-quick-add], .product-grid__item form[action*="/cart/add"] button[type="submit"]');
      if (!quickAddBtn) return;

      // Find the parent product card
      const card = quickAddBtn.closest('.product-grid__item');
      if (!card) return;

      const productId = card.dataset.productId;
      if (!productId) return;

      // Prevent the native cart add
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      // Find or click the bundle ADD button instead
      const bundleBtn = card.querySelector('[data-wsh-bundle-add]');
      if (bundleBtn) {
        bundleBtn.click();
      }
    }, true); // Use capture phase to intercept before Horizon's handlers

    // Intercept form submissions within product grid
    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (!form.matches('.product-grid__item form[action*="/cart/add"]')) return;

      const card = form.closest('.product-grid__item');
      if (!card) return;

      e.preventDefault();
      e.stopPropagation();

      const bundleBtn = card.querySelector('[data-wsh-bundle-add]');
      if (bundleBtn) {
        bundleBtn.click();
      }
    }, true);

    // Intercept fetch calls to /cart/add.js from within the product grid context
    const originalFetch = window.fetch;
    window.fetch = function(url, options) {
      // Only intercept if it's a cart add AND we're on the bundle page
      if (typeof url === 'string' && url.includes('/cart/add') && options?.body) {
        // Check if this came from a Quick Add (not from our bundle CTA)
        try {
          const body = typeof options.body === 'string' ? JSON.parse(options.body) : null;
          // Our bundle CTA adds items with _bundle_token property
          // Quick Add does not — intercept those
          if (body && body.items && !body.items[0]?.properties?._bundle_token) {
            // This is a Quick Add — block it silently
            return Promise.resolve(new Response(JSON.stringify({ items: [] }), { status: 200 }));
          }
          if (body && body.id && !body.properties?._bundle_token) {
            return Promise.resolve(new Response(JSON.stringify({ id: body.id }), { status: 200 }));
          }
        } catch (e) {
          // Not JSON — let it through
        }
      }
      return originalFetch.apply(this, arguments);
    };
  }

  /* Re-inject after quick filter AJAX DOM swap (Standing Rule 5).
     Filters trigger innerHTML replacement of the main section.
     The sidebar (appended by JS) is destroyed — re-attach it.
     The MutationObserver is watching a detached grid — re-observe. */
  document.addEventListener('filter:update', () => {
    // Delay to let DOM settle after section innerHTML swap.
    // 300ms gives Horizon's morphing/hydration time to complete.
    setTimeout(() => {
      measureStickyOffset(); // re-measure in case filter bar height changed
      setupDesktopLayout();  // re-attach sidebar
      injectAddButtons();
      syncAllButtons();
      observeGrid();         // re-observe new grid for infinite scroll
      // If currency changed, re-fetch prices for existing bundle items
      if (invalidatePriceCacheIfCurrencyChanged()) {
        refreshBundlePrices();
      }
    }, 300);
  });

  /* MutationObserver for infinite scroll card injection.
     Only watches direct children (new <li> cards added by infinite scroll).
     Does NOT watch subtree to avoid infinite loops from button injection. */
  let observerBusy = false;
  function observeGrid() {
    const grid = document.querySelector('.product-grid');
    if (!grid) return;
    const observer = new MutationObserver((mutations) => {
      if (observerBusy) return;
      // Only act if new child nodes were actually added
      const hasNewNodes = mutations.some((m) => m.addedNodes.length > 0);
      if (!hasNewNodes) return;
      observerBusy = true;
      requestAnimationFrame(() => {
        injectAddButtons();
        syncAllButtons();
        observerBusy = false;
      });
    });
    observer.observe(grid, { childList: true });
  }

  /* ========================================================================
     Pack list remove handler — ONLY way to remove from bundle
     ======================================================================== */
  document.addEventListener('click', async (e) => {
    const removeBtn = e.target.closest('[data-bp-remove]');
    if (!removeBtn) return;
    e.preventDefault();

    const productId = removeBtn.dataset.bpRemove;
    const idx = bundle.items.findIndex((i) => i.productId === productId);
    if (idx >= 0) {
      try {
        await removeItemFromCart(bundle.items[idx].lineItemKey);
        await readBundleFromCart(); // re-read for authoritative prices
      } catch (err) {
        console.error('Bundle remove failed:', err);
      }
    }
  });

  /* ========================================================================
     Summary update — renders both desktop sidebar and mobile sheet
     ======================================================================== */
  function updateAllSummaries() {
    updateSummary('');       // desktop (data-bp-*)
    updateSummary('-sheet'); // mobile sheet (data-bp-sheet-*)
    updateMobileBar();
    updatePackListScroll();  // toggle scroll indicator on desktop pack list
  }

  /** Toggle scroll indicator class when pack list overflows (Issue 5) */
  function updatePackListScroll() {
    const packList = $('[data-bp-pack-list]');
    if (!packList) return;
    // Defer to next frame so the DOM has settled after innerHTML update
    requestAnimationFrame(() => {
      packList.classList.toggle('wsh-bp__pack-list--scrollable',
        packList.scrollHeight > packList.clientHeight + 4);
    });
  }

  function updateSummary(prefix) {
    const p = prefix;
    const count = bundle.items.length;
    const copy = getStateCopy(count);
    const pct = getDiscountPct(count);

    const titleEl = $(`[data-bp${p}-title]`);
    const subcopyEl = $(`[data-bp${p}-subcopy]`);
    if (titleEl) titleEl.textContent = copy.title;
    if (subcopyEl) subcopyEl.textContent = copy.subcopy;

    const progressEl = $(`[data-bp${p}-progress]`);
    if (progressEl) {
      progressEl.style.width = Math.min((count / 4) * 100, 100) + '%';
    }

    $$(`[data-bp${p}-tier]`).forEach((tier) => {
      const tierCount = parseInt(tier.getAttribute(`data-bp${p}-tier`), 10);
      tier.classList.toggle('wsh-bp__tier--active', count >= tierCount);
    });

    const packHeader = $(`[data-bp${p}-pack-header]`);
    if (packHeader) {
      packHeader.textContent = `YOUR BUNDLE \u00b7 ${count} PACK${count !== 1 ? 'S' : ''}`;
    }

    const packList = $(`[data-bp${p}-pack-list]`);
    if (packList) {
      // Per Bundle Price Display rule: no per-pack prices anywhere on bundle
      // surfaces. Pack rows show thumb + title + remove only.
      packList.innerHTML = bundle.items.map((item) => {
        return `
          <li class="wsh-bp__pack-item">
            <div class="wsh-bp__pack-thumb">
              <img src="${escapeHtml(item.imageUrl)}" alt="${escapeHtml(item.title)}" width="32" height="32" loading="lazy">
            </div>
            <div class="wsh-bp__pack-info">
              <span class="wsh-bp__pack-name">${escapeHtml(item.title)}</span>
            </div>
            <button class="wsh-bp__pack-remove" data-bp-remove="${escapeHtml(item.productId)}" aria-label="Remove ${escapeHtml(item.title)} from bundle" type="button">&times;</button>
          </li>
        `;
      }).join('');
    }

    const encourageEl = $(`[data-bp${p}-encourage]`);
    if (encourageEl) {
      if (copy.encourage) {
        encourageEl.textContent = copy.encourage;
        encourageEl.classList.remove('wsh-bp__encourage--hidden');
      } else {
        encourageEl.classList.add('wsh-bp__encourage--hidden');
      }
    }

    /* Totals — running bundle price + chip only. Gated to 2+ packs so
       0/1-pack states show prompt + ladder with no price anywhere. */
    const totalsEl = $(`[data-bp${p}-totals]`);
    if (totalsEl) {
      if (count >= 2) {
        totalsEl.classList.remove('wsh-bp__totals--hidden');
        const total = bundle.items.reduce((sum, i) => sum + i.discountedPrice, 0);
        const totalEl = $(`[data-bp${p}-total]`);
        const chipEl = $(`[data-bp${p}-chip]`);
        if (totalEl) totalEl.textContent = formatMoney(total);
        if (chipEl) {
          if (pct > 0) {
            chipEl.textContent = `SAVE ${pct}%`;
            chipEl.style.display = '';
          } else {
            chipEl.style.display = 'none';
          }
        }
      } else {
        totalsEl.classList.add('wsh-bp__totals--hidden');
        const chipEl = $(`[data-bp${p}-chip]`);
        if (chipEl) chipEl.style.display = 'none';
      }
    }

    const ctaEl = $(`[data-bp${p}-cta]`);
    const ctaTextEl = $(`[data-bp${p}-cta-text]`);
    if (ctaEl && ctaTextEl) {
      if (copy.ctaDisabled) {
        ctaEl.classList.add('wsh-bp__cta--disabled');
        ctaEl.disabled = true;
        ctaTextEl.textContent = copy.cta;
        // Remove secondary line if present
        const secondary = ctaEl.parentElement?.querySelector('.wsh-bp__cta-secondary');
        if (secondary) secondary.remove();
      } else {
        ctaEl.classList.remove('wsh-bp__cta--disabled');
        ctaEl.disabled = false;
        const total = bundle.items.reduce((sum, i) => sum + i.discountedPrice, 0);
        ctaTextEl.textContent = `VIEW CART \u2014 ${formatMoney(total)}`;
        // Add/update secondary micro-line
        let secondary = ctaEl.parentElement?.querySelector('.wsh-bp__cta-secondary');
        if (!secondary) {
          secondary = document.createElement('p');
          secondary.className = 'wsh-bp__cta-secondary';
          ctaEl.insertAdjacentElement('afterend', secondary);
        }
        secondary.textContent = 'Your bundle is saved \u00b7 Keep adding to build';
      }
    }
  }

  /* ========================================================================
     Mobile slim bar
     ======================================================================== */
  function updateMobileBar() {
    const count = bundle.items.length;
    const copy = getStateCopy(count);
    const pct = getDiscountPct(count);

    const statusEl = $('[data-bp-mobile-status]');
    const nudgeEl = $('[data-bp-mobile-nudge]');
    const totalEl = $('[data-bp-mobile-total]');

    if (statusEl) {
      if (count >= 4) {
        statusEl.textContent = `${count} packs \u00b7 ${pct}% off`;
      } else {
        statusEl.textContent = copy.mobileStatus;
      }
    }

    if (nudgeEl) nudgeEl.textContent = copy.mobileNudge;

    if (totalEl) {
      const total = bundle.items.reduce((sum, i) => sum + i.discountedPrice, 0);
      totalEl.textContent = formatMoney(total);
    }
  }

  function showMobileFlash() {
    const flash = $('[data-bp-mobile-flash]');
    if (!flash) return;
    const count = bundle.items.length;
    const copy = getStateCopy(count);
    flash.textContent = `\u2713 Added \u2014 ${copy.encourage || 'Maximum discount'}`;
    flash.classList.add('wsh-bp__mobile-bar-flash--active');
    setTimeout(() => flash.classList.remove('wsh-bp__mobile-bar-flash--active'), 1500);
  }

  /* ========================================================================
     Mobile sheet open/close
     ======================================================================== */
  function openMobileSheet() {
    const sheet = $('[data-bp-mobile-sheet]');
    const overlay = $('[data-bp-mobile-overlay]');
    if (!sheet) return;

    overlay?.classList.add('wsh-bp__mobile-overlay--active');
    sheet.classList.add('wsh-bp__mobile-sheet--open');

    const focusable = sheet.querySelectorAll('button, [tabindex]:not([tabindex="-1"])');
    if (focusable.length) focusable[0].focus();

    sheet._trapFocus = (e) => {
      if (e.key === 'Escape') { closeMobileSheet(); return; }
      if (e.key !== 'Tab') return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus();
      }
    };
    document.addEventListener('keydown', sheet._trapFocus);
  }

  function closeMobileSheet() {
    const sheet = $('[data-bp-mobile-sheet]');
    const overlay = $('[data-bp-mobile-overlay]');
    if (!sheet) return;

    sheet.classList.remove('wsh-bp__mobile-sheet--open');
    overlay?.classList.remove('wsh-bp__mobile-overlay--active');

    if (sheet._trapFocus) {
      document.removeEventListener('keydown', sheet._trapFocus);
      sheet._trapFocus = null;
    }
    $('[data-bp-mobile-bar]')?.focus();
  }

  /* Bind mobile bar tap + overlay + close X */
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-bp-mobile-bar]')) { openMobileSheet(); return; }
    if (e.target.closest('[data-bp-mobile-overlay]')) { closeMobileSheet(); return; }
    if (e.target.closest('[data-bp-mobile-close]')) { closeMobileSheet(); return; }
  });

  /* ========================================================================
     Soundbar offset detection
     ======================================================================== */
  function updateSoundbarOffset() {
    const soundbar = document.querySelector('.soundbar');
    if (!soundbar) {
      document.documentElement.style.setProperty('--wsh-soundbar-offset', '0px');
      return;
    }
    const style = getComputedStyle(soundbar);
    const isVisible = style.visibility === 'visible' && !style.transform.includes('100%');
    const height = isVisible ? soundbar.offsetHeight : 0;
    document.documentElement.style.setProperty('--wsh-soundbar-offset', height + 'px');
  }

  let soundbarPollId = null;
  function startSoundbarPoll() {
    if (soundbarPollId) return;
    updateSoundbarOffset();
    soundbarPollId = setInterval(updateSoundbarOffset, 500);
  }

  /* ========================================================================
     Sticky offset measurement (Issue 5)
     Measures the sticky nav + sticky filter bar heights and sets
     --wsh-bp-sticky-top on <html> so CSS can position the sidebar correctly.
     Idempotent — safe to call on every re-render (Standing Rule 5).
     ======================================================================== */
  function measureStickyOffset() {
    if (window.innerWidth < 990) return;

    // Header height — Horizon sets this CSS variable on <body>
    const headerHeight = parseInt(
      getComputedStyle(document.body).getPropertyValue('--header-height') || '60', 10
    );

    // Filter bar — measure the section wrapper (sticky element), not inner content
    const filterBar = document.querySelector('[data-wsh-quick-filters]');
    const filterSection = filterBar?.closest('.shopify-section');
    const filterBarHeight = filterSection ? filterSection.offsetHeight : 0;

    // Combined offset + 8px breathing room
    const stickyTop = headerHeight + filterBarHeight + 8;
    document.documentElement.style.setProperty('--wsh-bp-sticky-top', stickyTop + 'px');
  }

  // Re-measure on resize (debounced)
  let resizeTimer = null;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(measureStickyOffset, 150);
  });

  /* ========================================================================
     CTA — opens cart drawer (items already in cart from individual adds)
     ======================================================================== */
  document.addEventListener('click', (e) => {
    const cta = e.target.closest('[data-bp-cta], [data-bp-sheet-cta]');
    if (!cta || cta.disabled) return;
    e.preventDefault();
    openCartFromBundle();
  });

  function openCartFromBundle() {
    closeMobileSheet();
    const cartDrawer = document.querySelector('cart-drawer-component');
    if (cartDrawer?.showDialog) {
      cartDrawer.showDialog();
    } else {
      window.location.href = '/cart';
    }
  }

  /* ========================================================================
     Desktop layout — move sidebar into the main-collection section wrapper.
     Persistent references survive innerHTML replacement (Standing Rule 5).
     MutationObserver re-attaches sidebar IMMEDIATELY when innerHTML
     replaces section children — no timeout, no race conditions.
     ======================================================================== */
  let sidebarElement = null;
  let mainSectionElement = null;
  let sectionObserver = null;
  let reattaching = false; // guard against observer re-entry

  function setupDesktopLayout() {
    if (window.innerWidth < 990) return;

    // Capture sidebar reference once — persists even after innerHTML detaches it
    if (!sidebarElement) {
      sidebarElement = document.querySelector('[data-wsh-bundle-sidebar]');
    }

    // Capture section wrapper reference once — it never changes (innerHTML
    // replaces children, not the wrapper itself)
    if (!mainSectionElement) {
      mainSectionElement = document.querySelector('.product-grid-container')
        ?.closest('[id^="shopify-section"]');
    }

    if (!sidebarElement || !mainSectionElement) return;

    // Move sidebar if not already in place
    if (!mainSectionElement.contains(sidebarElement)) {
      reattaching = true;
      mainSectionElement.appendChild(sidebarElement);
      reattaching = false;
    }

    mainSectionElement.classList.add('wsh-bp-layout');

    // ALWAYS assert grid layout via inline styles — server-rendered <style>
    // tags use ID-scoped rules (#shopify-section-...) that override class-based
    // CSS even with !important. Inline !important is the highest specificity.
    // This must run on initial load AND after every re-render.
    mainSectionElement.style.setProperty('display', 'grid', 'important');
    mainSectionElement.style.setProperty('grid-template-columns', '1fr 320px', 'important');
    mainSectionElement.style.setProperty('gap', '0 16px', 'important');
    mainSectionElement.style.setProperty('align-items', 'start', 'important');

    // Watch for innerHTML replacements that remove the sidebar.
    // The Section Rendering API wraps its response in a <div id="shopify-section-...">
    // which creates nesting when set as innerHTML. The nested wrapper's <style> tag
    // overrides our grid layout (ID specificity beats class specificity).
    // Fix: flatten the nesting, then re-append the sidebar.
    if (!sectionObserver) {
      sectionObserver = new MutationObserver(() => {
        if (reattaching) return;
        reattaching = true;

        // Flatten nested section wrapper created by Section Rendering API
        const nested = mainSectionElement.querySelector(':scope > [id^="shopify-section"]');
        if (nested) {
          while (nested.firstChild) {
            mainSectionElement.insertBefore(nested.firstChild, nested);
          }
          nested.remove();
        }

        // Re-append sidebar if detached
        if (!mainSectionElement.contains(sidebarElement)) {
          mainSectionElement.appendChild(sidebarElement);
        }

        // Re-assert grid layout via inline styles — the flattened <style> tag
        // contains ID-scoped rules (#shopify-section-...) that override our
        // class-based .wsh-bp-layout CSS. Inline !important beats everything.
        mainSectionElement.style.setProperty('display', 'grid', 'important');
        mainSectionElement.style.setProperty('grid-template-columns', '1fr 320px', 'important');
        mainSectionElement.style.setProperty('gap', '0 16px', 'important');
        mainSectionElement.style.setProperty('align-items', 'start', 'important');

        reattaching = false;

        // Re-inject ADD buttons on new product cards
        injectAddButtons();
        syncAllButtons();
        observeGrid();
      });
      sectionObserver.observe(mainSectionElement, { childList: true });
    }
  }

  /* ========================================================================
     Init
     ======================================================================== */
  async function init() {
    measureStickyOffset();
    setupDesktopLayout();
    interceptQuickAdd();
    injectAddButtons();
    observeGrid();
    startSoundbarPoll();
    // Read cart first, then sync UI — cart is source of truth
    await readBundleFromCart();
    // Safety: re-assert layout after cart read completes, in case Horizon's
    // hydration re-rendered the section during the async fetch
    measureStickyOffset();
    setupDesktopLayout();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
